import { ChangeEventHandler, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Box, IconButton, Paper, PaperProps, styled, Tooltip } from '@mui/material';
import classNames from 'classnames';

import { Icon, Select } from '@components/base';
import { SearchLanguage, SearchLanguages } from '@models/CountriesAndLanguages';
import FilterDefinition from '@models/FilterDefinition';
import { FilterField } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getNewFilterGroup } from '@utils/filterUtils';

import { useFilterPaperElevation } from './hooks/useFilterPaperElevation';
import FilterInputs, { columnCount } from './FilterInputs';

type FilterRowProps = {
  autoFocus?: boolean;
  disableIndent?: boolean;
  filterDefinitionPath?: FilterDefinition[];
  filterDefinitions: FilterDefinition[];
  filterField: FilterField;
  nestingLevel: number;
  onChange: (filterField: FilterField) => void;
  onRemove?: () => void;
};

type FilterPaperProps = PaperProps & Pick<FilterRowProps, 'nestingLevel'>;

const FilterPaper = styled(({ nestingLevel, ...props }: FilterPaperProps) => <Paper {...props} />)`
  display: grid;
  max-width: 100%;
  padding: ${props => props.theme.spacing(1.5, 1, 1)};

  ${({ theme }) =>
    theme.palette.mode === 'light' &&
    `
  background: ${alpha(theme.palette.common.white, 0.5)};
  `}
`;

const FilterRowContent = styled('div')`
  gap: ${({ theme }) => theme.spacing()};
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  align-items: flex-end;
`;

const FilterRow: FC<FilterRowProps> = ({
  autoFocus,
  filterDefinitionPath = [],
  filterDefinitions,
  filterField,
  nestingLevel,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const elevation = useFilterPaperElevation(nestingLevel);
  const { isReadonly } = useSearchAndFilter();

  const filterDefinition = filterDefinitions.find(
    definition => definition.propertyName === filterField.propertyName
  );

  const handleOnPropertyNameChange: ChangeEventHandler<HTMLInputElement> = ({
    target: { value: propertyName },
  }) => {
    const newFilterDefinition = filterDefinitions.find(
      definition => definition.propertyName === propertyName
    );

    if (newFilterDefinition)
      onChange({
        id: filterField.id,
        propertyName,
        operator: newFilterDefinition.operators[0],
        value: '',
        ...(newFilterDefinition.type === 'collection' || newFilterDefinition.type === 'object'
          ? { filter: getNewFilterGroup() }
          : {}),
      });
  };

  const isLanguageField = !filterDefinitions.some(
    ({ propertyName }) => !SearchLanguages.includes(propertyName as SearchLanguage)
  );

  return (
    <FilterPaper
      className={classNames({ root: !nestingLevel, nested: nestingLevel })}
      elevation={elevation}
      nestingLevel={nestingLevel}
      variant='elevation'
    >
      <FilterRowContent>
        <Select
          autoFocus={autoFocus}
          disabled={isReadonly}
          label={isLanguageField ? t('language') : t('field')}
          onChange={handleOnPropertyNameChange}
          options={filterDefinitions.map(definition => ({
            label: definition.displayName,
            value: definition.propertyName,
          }))}
          value={filterField.propertyName}
        />

        {filterDefinition && (
          <FilterInputs
            filterDefinition={filterDefinition}
            filterDefinitionPath={filterDefinitionPath}
            filterField={filterField}
            nestingLevel={nestingLevel}
            onChange={onChange}
            onRemove={onRemove}
          />
        )}

        {!isReadonly && !filterField.propertyName && onRemove && (
          <Box gridColumn={columnCount} ml='auto' my='auto'>
            <Tooltip placement='left' title={t('filter.remove')}>
              <IconButton onClick={onRemove} size='small'>
                <Icon fontSize='inherit' name='close' />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </FilterRowContent>
    </FilterPaper>
  );
};

export default FilterRow;
